import React, { useState, useEffect } from 'react'
import { AccountServicesType } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import { Chevron } from '../../icons/Chevron'
import PaymentsIcon from '../../icons/nav/Payments'
import { CryptoFiatIcon } from '../../icons/CryptoFiatIcon'

export const AccountServicesSelection = ({ onContinue }: { onContinue?:
(id: AccountServicesType) => void }): JSX.Element => {
  const [selectedService, setSelectedService] = useState<AccountServicesType | null>(null)
  const [hoveredService, setHoveredService] = useState<AccountServicesType | null>(null)

  const services = [
    {
      id: AccountServicesType.crypto_only,
      title: 'Crypto Only',
      description: 'Core crypto functionality with lighter KYB requirements.',
      features: [
        'Generate crypto payment links, invoices, checkout sessions, and other payment products',
        'Execute individual or mass crypto payouts',
        'Create dedicated crypto deposit addresses/business wallets'
      ],
      badge: 'Popular',
      badgeColor: '#10b981',
      badgeBg: '#d1fae5',
      footerInfo: 'Crypto-only KYB submissions typically take 1 \n business day to be reviewed and approved.'
    },
    {
      id: AccountServicesType.crypto_and_fiat,
      title: 'Crypto & Fiat',
      description: 'Full crypto and fiat functionality with enhanced KYB requirements',
      features: [
        'Gain access to our full suite of crypto services',
        'Instantly move from fiat to crypto using virtual bank accounts in USD, EUR, GBP, BRL, and MXN',
        'Make individual or mass fiat payouts using stablecoins as the funding source',
        'Access advanced conversion functionality for seamless crypto-to-crypto & crypto-to-fiat exchange and settlement.'

      ],
      badge: 'Recommended',
      badgeColor: '#3b82f6',
      badgeBg: '#dbeafe',
      footerInfo: 'Crypto & fiat KYB submissions typically take \n3–5 business days to be reviewed and approved.'
    }
  ]

  const getCardStyle = (service): React.CSSProperties => {
    const isSelected = selectedService === service.id
    const isHovered = hoveredService === service.id

    return {
      position: 'relative',
      borderRadius: '14px',
      background: 'white',
      padding: '20px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: isSelected ? `2px solid ${RADOM_COLORS.RADOM_BLUE}` : isHovered ? '2px solid #93c5fd' : '2px solid #e5e7eb',
      boxShadow: isSelected
        ? '0 20px 25px -5px rgba(59, 130, 246, 0.3), 0 10px 10px -5px rgba(59, 130, 246, 0.2)'
        : isHovered
          ? '0 10px 15px -3px rgba(59, 130, 246, 0.2), 0 4px 6px -2px rgba(59, 130, 246, 0.1)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
      height: '500px',
      maxWidth: '400px',
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      gap: '13px',
      flexDirection: 'column'
    }
  }

  const [gridTemplateColumns, setGridTemplateColumns] = useState<string>('1fr')
  const [gridGap, setGridGap] = useState<string>('24px')

  useEffect(() => {
    const updateGrid = (): void => {
      const w = window.innerWidth
      if (w >= 1024) {
        setGridTemplateColumns('repeat(2, 1fr)')
        setGridGap('140px')
      } else if (w >= 768) {
        setGridTemplateColumns('repeat(2, 1fr)')
        setGridGap('48px')
      } else {
        setGridTemplateColumns('1fr')
        setGridGap('24px')
      }
    }

    updateGrid()
    window.addEventListener('resize', updateGrid)
    return () => window.removeEventListener('resize', updateGrid)
  }, [])

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
    }}>
      <div style={{
        background: 'white',
        borderBottom: '1px solid #D5D7E8',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          margin: '0 auto',
          padding: '16px 24px'
        }}>
          <button style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            color: 'black'
          }}
          onClick={() => window.history.back()}
          >
            <Chevron style={{ transform: 'rotate(90deg)' }} />
            <span>Verify organization
            </span>
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '32px 24px'
      }}>

        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: '20px',
            fontWeight: '300',
            marginBottom: '10px'
          }}>
            Choose Your Account Services
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#808080',
            fontWeight: '300'
          }}>
            Select the service level that best matches your business needs.<br /> You can always upgrade later.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns,
          gap: gridGap
        }}>
          {services.map((service) => {
            const isSelected = selectedService === service.id

            return (
              <div
                key={service.id}
                onClick={() => setSelectedService(service.id)}
                onMouseEnter={() => setHoveredService(service.id)}
                onMouseLeave={() => setHoveredService(null)}
                style={getCardStyle(service)}
              >
                <div style={{
                  position: 'absolute',
                  top: '24px',
                  right: '24px'
                }}>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '400',
                    background: service.badgeBg,
                    color: service.badgeColor
                  }}>
                    {service.badge}
                  </span>
                </div>

                <div style={{
                  width: '66px',
                  height: '66px',
                  borderRadius: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isSelected ? RADOM_COLORS.RADOM_BLUE : RADOM_COLORS.GRAY_LIGHTEST,
                  fontSize: '32px',
                  flexShrink: 0,
                  transition: 'background 0.3s'
                }}>
                  {service.id === AccountServicesType.crypto_only
                    ? <PaymentsIcon width={44} stroke={isSelected ? 'white' : '#747894'} />
                    : <CryptoFiatIcon width={44} stroke={isSelected ? 'white' : '#747894'} />}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>

                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '16px',
                    margin: 0
                  }}>
                    {service.title}
                  </h3>

                  <p style={{
                    margin: 0,
                    color: '#808080',
                    lineHeight: '16px',
                    fontSize: '14px',
                    fontWeight: '300'
                  }}>
                    {service.description}
                  </p>

                </div>

                <ul style={{
                  padding: 0,
                  margin: 0,
                  listStyle: 'none'
                }}>
                  {service.features.map((feature, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontSize: '14px',
                      fontWeight: '300',
                      marginBottom: '8px',
                      lineHeight: '1.5'
                    }}>
                      <div style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: 'black',
                        marginTop: '8px',
                        flexShrink: 0
                      }} />

                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div style={{
                  display: 'flex',
                  padding: '0 16px',
                  height: '68px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '10px',
                  border: '1px solid rgba(42, 99, 255, 0.30)',
                  background: '#E7EDFF',
                  marginTop: 'auto'
                }}>
                  <span style={{ color: RADOM_COLORS.RADOM_BLUE, fontSize: '14px', fontWeight: '300', lineHeight: '20px' }}>{service.footerInfo}</span>
                </div>

              </div>
            )
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '48px' }}>
          <button
            onClick={() => selectedService && onContinue && onContinue(selectedService)}
            disabled={!selectedService}
            style={{
              padding: '16px 32px',
              borderRadius: '12px',
              fontWeight: '300',
              fontSize: '18px',
              border: 'none',
              cursor: selectedService ? 'pointer' : 'not-allowed',
              background: selectedService ? RADOM_COLORS.NEW_BLUE : '#e5e7eb',
              color: selectedService ? 'white' : '#9ca3af',
              boxShadow: selectedService ? '0 10px 15px -3px rgba(59, 130, 246, 0.3)' : 'none',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              if (selectedService) {
                e.currentTarget.style.background = '#2563eb'
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(59, 130, 246, 0.4)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseLeave={(e) => {
              if (selectedService) {
                e.currentTarget.style.background = RADOM_COLORS.NEW_BLUE
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
                e.currentTarget.style.transform = 'translateY(0)'
              }
            }}
          >
            Continue with {selectedService === AccountServicesType.crypto_only ? 'Crypto Only' : selectedService === AccountServicesType.crypto_and_fiat ? 'Crypto & Fiat' : 'Selected Service'}
          </button>
        </div>
      </div>
    </div>
  )
}
